/* =========================================================================
   chart-export.ts — SVG → PNG data URL for embedding in pdfmake.
   ========================================================================= */

const SVG_NS = "http://www.w3.org/2000/svg";

function cloneSvgForExport(svg: SVGSVGElement): SVGSVGElement {
  const clone = svg.cloneNode(true) as SVGSVGElement;

  // Inline computed styles so the canvas render matches the DOM
  const sourceStyles = window.getComputedStyle(svg);
  clone.style.fontFamily = sourceStyles.fontFamily;
  clone.style.color = sourceStyles.color;

  // Ensure namespace is present
  if (!clone.getAttribute("xmlns")) clone.setAttribute("xmlns", SVG_NS);

  // Set explicit width/height from viewBox if missing, so canvas knows the size
  const vb = clone.getAttribute("viewBox");
  if (vb && (!clone.getAttribute("width") || !clone.getAttribute("height"))) {
    const parts = vb.split(/\s+/).map(Number);
    if (parts.length === 4) {
      if (!clone.getAttribute("width"))
        clone.setAttribute("width", String(parts[2]));
      if (!clone.getAttribute("height"))
        clone.setAttribute("height", String(parts[3]));
    }
  }

  return clone;
}

export async function svgElementToPngDataUrl(
  svg: SVGSVGElement,
  scale = 2,
): Promise<string> {
  const clone = cloneSvgForExport(svg);
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(clone);
  const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const width = parseFloat(clone.getAttribute("width") || "0");
  const height = parseFloat(clone.getAttribute("height") || "0");
  if (!width || !height) {
    URL.revokeObjectURL(url);
    throw new Error("SVG has no explicit width/height or viewBox");
  }

  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(width * scale);
  canvas.height = Math.ceil(height * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    URL.revokeObjectURL(url);
    throw new Error("Could not get 2D canvas context");
  }

  const img = new Image();
  let resolved = false;
  const loaded = new Promise<string>((resolve, reject) => {
    img.onload = () => {
      try {
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL("image/png");
        resolved = true;
        resolve(dataUrl);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      reject(new Error("Failed to load SVG into image"));
    };
  });

  img.src = url;
  try {
    return await loaded;
  } finally {
    URL.revokeObjectURL(url);
    if (resolved) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}

/**
 * Capture the first SVG element matching a selector as a PNG data URL.
 * Returns null if no SVG found.
 */
export async function captureSvgSelector(
  selector: string,
  scale = 2,
): Promise<string | null> {
  const el = document.querySelector(selector) as SVGSVGElement | null;
  if (!el) return null;
  return svgElementToPngDataUrl(el, scale);
}

/** Convenience: capture the first SVG with class .chart-wrap */
export async function captureChartWrap(scale = 2): Promise<string | null> {
  const el = document.querySelector("svg.chart-wrap") as SVGSVGElement | null;
  if (!el) return null;
  return svgElementToPngDataUrl(el, scale);
}

// ----- minimal tornado rendering (no React, no DOM dependency) -----

function nice(v: number): number {
  const sign = v < 0 ? -1 : 1;
  const abs = Math.abs(v);
  const mag = Math.pow(10, Math.floor(Math.log10(abs || 1)));
  const norm = abs / mag;
  let r: number;
  if (norm < 1.5) r = 1;
  else if (norm < 3) r = 2;
  else if (norm < 7) r = 5;
  else r = 10;
  return sign * r * mag;
}

function niceTicks(min: number, max: number, count = 5): number[] {
  if (min === max) return [min];
  const range = nice(max - min);
  const step = nice(range / (count - 1));
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const out: number[] = [];
  for (let v = niceMin; v <= niceMax + 1e-9; v += step) out.push(v);
  return out;
}

export function formatTickShort(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? "−" : "";
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(0)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}${Math.round(abs)}`;
}

export interface HistogramBin {
  min: number;
  max: number;
  count: number;
}

export function renderHistogramSvg(
  counts: number[],
  width = 520,
  height = 160,
  barColor = "hsl(140 60% 45%)",
  edges?: number[],
  valueFormat: (v: number) => string = (v: number) =>
    `${(v * 100).toFixed(1)}%`,
): SVGSVGElement {
  const pad = { l: 52, r: 16, t: 10, b: 28 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;

  if (!counts.length) {
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("width", String(width));
    svg.setAttribute("height", String(height));
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    const text = document.createElementNS(SVG_NS, "text");
    text.setAttribute("x", String(width / 2));
    text.setAttribute("y", String(height / 2));
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("fill", "#9ca3af");
    text.setAttribute("font-size", "11");
    text.setAttribute("font-family", "Roboto, Arial, sans-serif");
    text.textContent = "No data";
    svg.appendChild(text);
    return svg;
  }

  const maxCount = Math.max(...counts, 1);

  // Build bins from counts + edges, or infer equal-width bins
  const bins: HistogramBin[] = [];
  if (edges && edges.length === counts.length + 1) {
    for (let i = 0; i < counts.length; i++) {
      bins.push({ min: edges[i], max: edges[i + 1], count: counts[i] });
    }
  } else {
    for (let i = 0; i < counts.length; i++) {
      bins.push({ min: i, max: i + 1, count: counts[i] });
    }
  }

  const minVal = bins[0].min;
  const maxVal = bins[bins.length - 1].max;
  const valRange = maxVal - minVal;

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);

  // Grid lines
  const gridG = document.createElementNS(SVG_NS, "g");
  gridG.setAttribute("stroke", "#e8ebf0");
  gridG.setAttribute("stroke-width", "1");
  for (let i = 0; i <= 4; i++) {
    const y = pad.t + (innerH * i) / 4;
    const line = document.createElementNS(SVG_NS, "line");
    line.setAttribute("x1", String(pad.l));
    line.setAttribute("y1", String(y));
    line.setAttribute("x2", String(width - pad.r));
    line.setAttribute("y2", String(y));
    gridG.appendChild(line);
  }
  svg.appendChild(gridG);

  // Bars
  const barW = innerW / bins.length;
  const gap = Math.max(1, barW * 0.12);
  bins.forEach((bin) => {
    const barH = (bin.count / maxCount) * innerH;
    const x =
      pad.l + (valRange > 0 ? ((bin.min - minVal) / valRange) * innerW : 0);
    const y = pad.t + innerH - barH;

    const rect = document.createElementNS(SVG_NS, "rect");
    rect.setAttribute("x", String(x + gap / 2));
    rect.setAttribute("y", String(y));
    rect.setAttribute("width", String(Math.max(1, barW - gap)));
    rect.setAttribute("height", String(barH));
    rect.setAttribute("fill", barColor);
    rect.setAttribute("rx", String(Math.min(2, barW * 0.15)));
    svg.appendChild(rect);
  });

  // X axis ticks (3 ticks: min, mid, max)
  const tickCount = 3;
  for (let i = 0; i < tickCount; i++) {
    const v = valRange > 0 ? minVal + (i / (tickCount - 1)) * valRange : minVal;
    const x = pad.l + (i / (tickCount - 1)) * innerW;
    const text = document.createElementNS(SVG_NS, "text");
    text.setAttribute("x", String(x));
    text.setAttribute("y", String(height - pad.b + 14));
    text.setAttribute(
      "text-anchor",
      i === 0 ? "start" : i === tickCount - 1 ? "end" : "middle",
    );
    text.setAttribute("font-size", "9");
    text.setAttribute("fill", "#6b7280");
    text.setAttribute("font-family", "Roboto, Arial, sans-serif");
    text.textContent = valueFormat(v);
    svg.appendChild(text);
  }

  // Y axis label (max count)
  const yText = document.createElementNS(SVG_NS, "text");
  yText.setAttribute("x", String(pad.l - 6));
  yText.setAttribute("y", String(pad.t + 8));
  yText.setAttribute("text-anchor", "end");
  yText.setAttribute("font-size", "9");
  yText.setAttribute("fill", "#9ca3af");
  yText.setAttribute("font-family", "Roboto, Arial, sans-serif");
  yText.textContent = String(maxCount);
  svg.appendChild(yText);

  return svg;
}

export function renderTornadoSvg(
  rows: Array<{ label: string; low: number; high: number }>,
  base: number,
  width = 760,
  rowHeight = 28,
  valueFormat: (v: number) => string = formatTickShort,
  presentationBound?: number,
  deltaFormat: (v: number) => string = valueFormat,
): SVGSVGElement {
  const pad = { l: 220, r: 76, t: 12, b: 52 };
  const innerW = width - pad.l - pad.r;
  const minimumHeight = 160;
  const maximumHeight = 480;
  const naturalHeight = pad.t + pad.b + rowHeight * rows.length;
  const height = Math.min(
    maximumHeight,
    Math.max(minimumHeight, naturalHeight),
  );
  const effectiveRowHeight =
    rows.length > 0 ? (height - pad.t - pad.b) / rows.length : rowHeight;
  const dataMaxAbs = Math.max(
    ...rows.map((r) =>
      Math.max(Math.abs(r.high - base), Math.abs(r.low - base)),
    ),
    1,
  );
  const maxAbs =
    presentationBound !== undefined && presentationBound > 0
      ? presentationBound
      : dataMaxAbs;
  const ticks =
    presentationBound !== undefined && presentationBound > 0
      ? [-maxAbs, -maxAbs / 2, 0, maxAbs / 2, maxAbs]
      : niceTicks(-maxAbs, maxAbs, 5);
  const xScale = (v: number) => pad.l + ((v + maxAbs) / (2 * maxAbs)) * innerW;
  const x0 = xScale(0);

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  svg.setAttribute("xmlns", SVG_NS);
  svg.setAttribute("font-family", "Roboto, Arial, sans-serif");
  svg.style.fontFamily = "Roboto, Arial, sans-serif";

  // Grid lines
  ticks.forEach((tk) => {
    const line = document.createElementNS(SVG_NS, "line");
    line.setAttribute("x1", String(xScale(tk)));
    line.setAttribute("x2", String(xScale(tk)));
    line.setAttribute("y1", String(pad.t));
    line.setAttribute("y2", String(pad.t + rows.length * effectiveRowHeight));
    line.setAttribute("stroke", "hsl(220 15% 25% / 0.35)");
    line.setAttribute("stroke-dasharray", "2 3");
    svg.appendChild(line);
  });

  // Center baseline
  const baseline = document.createElementNS(SVG_NS, "line");
  baseline.setAttribute("x1", String(x0));
  baseline.setAttribute("x2", String(x0));
  baseline.setAttribute("y1", String(pad.t - 4));
  baseline.setAttribute(
    "y2",
    String(pad.t + rows.length * effectiveRowHeight + 4),
  );
  baseline.setAttribute("stroke", "hsl(220 10% 55%)");
  baseline.setAttribute("stroke-width", "1.5");
  svg.appendChild(baseline);

  // Rows
  rows.forEach((r, i) => {
    const yMid = pad.t + i * effectiveRowHeight + effectiveRowHeight / 2;
    // P7-R1 (#14/#15): encode by OUTCOME — worse (lower KPI) red/left, better (higher) green/right.
    const worse = Math.min(r.low, r.high);
    const better = Math.max(r.low, r.high);
    const lW = Math.abs(xScale(worse - base) - x0);
    const hW = Math.abs(xScale(better - base) - x0);

    // Label
    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", String(pad.l - 12));
    label.setAttribute("y", String(yMid + 4));
    label.setAttribute("text-anchor", "end");
    label.setAttribute("font-size", "14");
    label.setAttribute("fill", "#2c3645"); // COLORS.dark — primary label, ~12:1 on white (PDF/Excel render on white, not SPA dark theme)
    label.textContent = r.label;
    svg.appendChild(label);

    // Low bar (red)
    const lowBar = document.createElementNS(SVG_NS, "rect");
    lowBar.setAttribute("x", String(x0 - lW));
    lowBar.setAttribute("y", String(yMid - 9));
    lowBar.setAttribute("width", String(lW));
    lowBar.setAttribute("height", "18");
    lowBar.setAttribute("fill", "hsl(0 72% 51% / 0.55)");
    lowBar.setAttribute("rx", "2");
    svg.appendChild(lowBar);

    // High bar (green)
    const highBar = document.createElementNS(SVG_NS, "rect");
    highBar.setAttribute("x", String(x0));
    highBar.setAttribute("y", String(yMid - 9));
    highBar.setAttribute("width", String(hW));
    highBar.setAttribute("height", "18");
    highBar.setAttribute("fill", "hsl(142 76% 50% / 0.6)");
    highBar.setAttribute("rx", "2");
    svg.appendChild(highBar);

    // Low value label
    const lowLabel = document.createElementNS(SVG_NS, "text");
    lowLabel.setAttribute("x", String(x0 - lW - 6));
    lowLabel.setAttribute("y", String(yMid + 4));
    lowLabel.setAttribute("text-anchor", "end");
    lowLabel.setAttribute("font-size", "13");
    lowLabel.setAttribute("fill", "#5b6779"); // COLORS.muted — secondary label, ~5.7:1 on white
    lowLabel.textContent = valueFormat(worse);
    svg.appendChild(lowLabel);

    // High value label
    const highLabel = document.createElementNS(SVG_NS, "text");
    highLabel.setAttribute("x", String(x0 + hW + 6));
    highLabel.setAttribute("y", String(yMid + 4));
    highLabel.setAttribute("text-anchor", "start");
    highLabel.setAttribute("font-size", "13");
    highLabel.setAttribute("fill", "#5b6779"); // COLORS.muted
    highLabel.textContent = valueFormat(better);
    svg.appendChild(highLabel);
  });

  // Bottom axis
  ticks.forEach((tk) => {
    const tick = document.createElementNS(SVG_NS, "text");
    tick.setAttribute("x", String(xScale(tk)));
    tick.setAttribute("y", String(height - 12));
    tick.setAttribute("text-anchor", "middle");
    tick.setAttribute("font-size", "13");
    tick.setAttribute("fill", "#5b6779"); // COLORS.muted — axis ticks
    const prefix = tk >= 0 ? "+" : "";
    tick.textContent = prefix + deltaFormat(tk).replace("−", "−");
    svg.appendChild(tick);
  });

  const axisLabel = document.createElementNS(SVG_NS, "text");
  axisLabel.setAttribute("x", String(pad.l + innerW / 2));
  axisLabel.setAttribute("y", String(height - 2));
  axisLabel.setAttribute("text-anchor", "middle");
  axisLabel.setAttribute("font-size", "13");
  axisLabel.setAttribute("font-weight", "600");
  axisLabel.setAttribute("fill", "#334155");
  axisLabel.textContent = `Change from base IRR · axis ±${(maxAbs * 100).toFixed(1)} pp`;
  svg.appendChild(axisLabel);

  return svg;
}

/**
 * Preserve every visibly decision-useful driver while consolidating rows whose
 * displayed movement would round below 0.1 percentage points.
 */
export function summarizeTornadoRowsForReport(
  rows: Array<{ label: string; low: number; high: number }>,
  base: number,
  visibleThreshold = 0.0005,
): Array<{ label: string; low: number; high: number }> {
  const visible = rows.filter(
    (row) =>
      Math.max(Math.abs(row.low - base), Math.abs(row.high - base)) >=
      visibleThreshold,
  );
  const suppressed = rows.length - visible.length;
  return suppressed > 0
    ? [
        ...visible,
        {
          label: `Other ${suppressed} drivers: <0.1 pp`,
          low: base,
          high: base,
        },
      ]
    : visible;
}

export function calculateSharedTornadoBound(
  datasets: Array<Array<{ low: number; high: number }>>,
  bases: number[],
): number {
  let largestDelta = 0;
  datasets.forEach((rows, datasetIndex) => {
    const base = bases[datasetIndex];
    if (!Number.isFinite(base)) return;
    rows.forEach(({ low, high }) => {
      for (const value of [low, high]) {
        if (Number.isFinite(value)) {
          largestDelta = Math.max(largestDelta, Math.abs(value - base));
        }
      }
    });
  });

  const minimum = 0.01;
  if (largestDelta <= minimum) return minimum;
  const interval =
    largestDelta <= 0.05
      ? 0.01
      : largestDelta <= 0.2
        ? 0.025
        : largestDelta <= 0.5
          ? 0.05
          : 0.1;
  return Number((Math.ceil(largestDelta / interval) * interval).toFixed(6));
}

/**
 * Render a minimal sparkline SVG (no axes, just the line).
 */
export function renderSparklineSvg(
  values: number[],
  width = 120,
  height = 30,
  color = "#7e6a3f",
): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  svg.setAttribute("xmlns", SVG_NS);

  if (values.length < 2) {
    const text = document.createElementNS(SVG_NS, "text");
    text.setAttribute("x", "50%");
    text.setAttribute("y", "50%");
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("font-size", "10");
    text.setAttribute("fill", "#888");
    text.textContent = "—";
    svg.appendChild(text);
    return svg;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pad = 2;

  const points = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (width - pad * 2);
    const y = height - pad - ((v - min) / range) * (height - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const polyline = document.createElementNS(SVG_NS, "polyline");
  polyline.setAttribute("points", points.join(" "));
  polyline.setAttribute("fill", "none");
  polyline.setAttribute("stroke", color);
  polyline.setAttribute("stroke-width", "2");
  polyline.setAttribute("stroke-linecap", "round");
  polyline.setAttribute("stroke-linejoin", "round");
  svg.appendChild(polyline);

  // End dot
  const last = points[points.length - 1].split(",").map(Number);
  const dot = document.createElementNS(SVG_NS, "circle");
  dot.setAttribute("cx", String(last[0]));
  dot.setAttribute("cy", String(last[1]));
  dot.setAttribute("r", "2.5");
  dot.setAttribute("fill", color);
  svg.appendChild(dot);

  return svg;
}

/* ── Full-size chart renderers for Excel embedding ── */

/** Render a cashflow chart: EBITDA bars + cumulative line. */
export function renderCashflowSvg(
  years: Array<{
    year: number;
    reddamEbitda: number;
    cumulativeCashflow: number;
    totalRevenue: number;
    rentGross: number;
  }>,
  width = 640,
  height = 320,
): SVGSVGElement {
  const pad = { l: 64, r: 16, t: 24, b: 40 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  svg.setAttribute("xmlns", SVG_NS);
  svg.setAttribute("font-family", "Roboto, Arial, sans-serif");
  svg.style.fontFamily = "Roboto, Arial, sans-serif";

  const ny = years.length;
  if (ny < 1) {
    const text = document.createElementNS(SVG_NS, "text");
    text.setAttribute("x", "50%");
    text.setAttribute("y", "50%");
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("font-size", "12");
    text.setAttribute("fill", "#888");
    text.textContent = "No data";
    svg.appendChild(text);
    return svg;
  }

  // Scales
  const minEbitda = Math.min(...years.map((y) => y.reddamEbitda), 0);
  const maxEbitda = Math.max(...years.map((y) => y.reddamEbitda), 0);
  const minCum = Math.min(...years.map((y) => y.cumulativeCashflow));
  const maxCum = Math.max(...years.map((y) => y.cumulativeCashflow));

  // Shared Y-axis that fits both EBITDA and cumulative
  const yMin = Math.min(minEbitda, minCum);
  const yMax = Math.max(maxEbitda, maxCum);
  const yRange = yMax - yMin || 1;

  const yScale = (v: number) => pad.t + innerH - ((v - yMin) / yRange) * innerH;
  const xScale = (i: number) => pad.l + (i / Math.max(1, ny - 1)) * innerW;
  const barW = Math.max(4, (innerW / Math.max(1, ny)) * 0.5);

  // Grid lines (horizontal)
  const yTicks = niceTicks(yMin, yMax, 5);
  yTicks.forEach((tk) => {
    const y = yScale(tk);
    const line = document.createElementNS(SVG_NS, "line");
    line.setAttribute("x1", String(pad.l));
    line.setAttribute("x2", String(width - pad.r));
    line.setAttribute("y1", String(y));
    line.setAttribute("y2", String(y));
    line.setAttribute("stroke", "hsl(220 15% 25% / 0.2)");
    line.setAttribute("stroke-dasharray", "2 3");
    svg.appendChild(line);

    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", String(pad.l - 8));
    label.setAttribute("y", String(y + 4));
    label.setAttribute("text-anchor", "end");
    label.setAttribute("font-size", "10");
    label.setAttribute("fill", "#94a3b8");
    label.textContent = formatTickShort(tk);
    svg.appendChild(label);
  });

  // Zero line
  const zeroY = yScale(0);
  const zeroLine = document.createElementNS(SVG_NS, "line");
  zeroLine.setAttribute("x1", String(pad.l));
  zeroLine.setAttribute("x2", String(width - pad.r));
  zeroLine.setAttribute("y1", String(zeroY));
  zeroLine.setAttribute("y2", String(zeroY));
  zeroLine.setAttribute("stroke", "hsl(220 10% 55% / 0.5)");
  svg.appendChild(zeroLine);

  // EBITDA bars
  years.forEach((y, i) => {
    const x = xScale(i);
    const barH = Math.abs(yScale(0) - yScale(y.reddamEbitda));
    const barY = y.reddamEbitda >= 0 ? yScale(y.reddamEbitda) : yScale(0);
    const rect = document.createElementNS(SVG_NS, "rect");
    rect.setAttribute("x", String(x - barW / 2));
    rect.setAttribute("y", String(barY));
    rect.setAttribute("width", String(barW));
    rect.setAttribute("height", String(barH));
    rect.setAttribute(
      "fill",
      y.reddamEbitda >= 0 ? "hsl(142 76% 50% / 0.7)" : "hsl(0 72% 51% / 0.7)",
    );
    rect.setAttribute("rx", "2");
    svg.appendChild(rect);
  });

  // Cumulative cashflow line
  const cumPoints = years
    .map((y, i) => {
      const x = xScale(i);
      const yp = yScale(y.cumulativeCashflow);
      return `${x.toFixed(1)},${yp.toFixed(1)}`;
    })
    .join(" ");

  const cumPoly = document.createElementNS(SVG_NS, "polyline");
  cumPoly.setAttribute("points", cumPoints);
  cumPoly.setAttribute("fill", "none");
  cumPoly.setAttribute("stroke", "hsl(210 80% 60%)");
  cumPoly.setAttribute("stroke-width", "2.5");
  cumPoly.setAttribute("stroke-linecap", "round");
  cumPoly.setAttribute("stroke-linejoin", "round");
  svg.appendChild(cumPoly);

  // End dot for cumulative
  const lastCum = cumPoints.split(" ").pop()?.split(",").map(Number) ?? [0, 0];
  const cumDot = document.createElementNS(SVG_NS, "circle");
  cumDot.setAttribute("cx", String(lastCum[0]));
  cumDot.setAttribute("cy", String(lastCum[1]));
  cumDot.setAttribute("r", "3.5");
  cumDot.setAttribute("fill", "hsl(210 80% 60%)");
  svg.appendChild(cumDot);

  // X-axis labels
  years.forEach((y, i) => {
    const x = xScale(i);
    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", String(x));
    label.setAttribute("y", String(height - 12));
    label.setAttribute("text-anchor", "middle");
    label.setAttribute("font-size", "10");
    label.setAttribute("fill", "#94a3b8");
    label.textContent = `Y${y.year}`;
    svg.appendChild(label);
  });

  // Legend
  const legendY = 14;
  const legendItems = [
    { color: "hsl(142 76% 50% / 0.7)", label: "EBITDA +" },
    { color: "hsl(0 72% 51% / 0.7)", label: "EBITDA −" },
    { color: "hsl(210 80% 60%)", label: "Cumulative" },
  ];
  legendItems.forEach((item, i) => {
    const lx = pad.l + i * 100;
    const dot = document.createElementNS(SVG_NS, "rect");
    dot.setAttribute("x", String(lx));
    dot.setAttribute("y", String(legendY - 6));
    dot.setAttribute("width", "10");
    dot.setAttribute("height", "10");
    dot.setAttribute("fill", item.color);
    dot.setAttribute("rx", "2");
    svg.appendChild(dot);

    const lbl = document.createElementNS(SVG_NS, "text");
    lbl.setAttribute("x", String(lx + 14));
    lbl.setAttribute("y", String(legendY + 3));
    lbl.setAttribute("font-size", "10");
    lbl.setAttribute("fill", "#94a3b8");
    lbl.textContent = item.label;
    svg.appendChild(lbl);
  });

  return svg;
}

/** Render a P&L bridge chart: Revenue, Rent, Costs, EBITDA as lines. */
export function renderPnLSvg(
  years: Array<{
    year: number;
    totalRevenue: number;
    rentGross: number;
    variableCosts: number;
    fixedOpex: number;
    fitoutAmortisation: number;
    reddamEbitda: number;
  }>,
  width = 640,
  height = 320,
): SVGSVGElement {
  const pad = { l: 64, r: 16, t: 24, b: 40 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  svg.setAttribute("xmlns", SVG_NS);
  svg.setAttribute("font-family", "Roboto, Arial, sans-serif");
  svg.style.fontFamily = "Roboto, Arial, sans-serif";

  const ny = years.length;
  if (ny < 1) {
    const text = document.createElementNS(SVG_NS, "text");
    text.setAttribute("x", "50%");
    text.setAttribute("y", "50%");
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("font-size", "12");
    text.setAttribute("fill", "#888");
    text.textContent = "No data";
    svg.appendChild(text);
    return svg;
  }

  // Scales
  const allValues = years.flatMap((y) => [
    y.totalRevenue,
    y.rentGross,
    y.variableCosts,
    y.fixedOpex,
    y.fitoutAmortisation,
    y.reddamEbitda,
  ]);
  const yMin = Math.min(...allValues, 0);
  const yMax = Math.max(...allValues);
  const yRange = yMax - yMin || 1;

  const yScale = (v: number) => pad.t + innerH - ((v - yMin) / yRange) * innerH;
  const xScale = (i: number) => pad.l + (i / Math.max(1, ny - 1)) * innerW;

  // Grid lines
  const yTicks = niceTicks(yMin, yMax, 5);
  yTicks.forEach((tk) => {
    const y = yScale(tk);
    const line = document.createElementNS(SVG_NS, "line");
    line.setAttribute("x1", String(pad.l));
    line.setAttribute("x2", String(width - pad.r));
    line.setAttribute("y1", String(y));
    line.setAttribute("y2", String(y));
    line.setAttribute("stroke", "hsl(220 15% 25% / 0.2)");
    line.setAttribute("stroke-dasharray", "2 3");
    svg.appendChild(line);

    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", String(pad.l - 8));
    label.setAttribute("y", String(y + 4));
    label.setAttribute("text-anchor", "end");
    label.setAttribute("font-size", "10");
    label.setAttribute("fill", "#94a3b8");
    label.textContent = formatTickShort(tk);
    svg.appendChild(label);
  });

  // Zero line
  const zeroY = yScale(0);
  const zeroLine = document.createElementNS(SVG_NS, "line");
  zeroLine.setAttribute("x1", String(pad.l));
  zeroLine.setAttribute("x2", String(width - pad.r));
  zeroLine.setAttribute("y1", String(zeroY));
  zeroLine.setAttribute("y2", String(zeroY));
  zeroLine.setAttribute("stroke", "hsl(220 10% 55% / 0.5)");
  svg.appendChild(zeroLine);

  // Series — primary metrics (solid) + cost breakdown (dashed)
  const series = [
    {
      key: "totalRevenue",
      color: "hsl(210 80% 60%)",
      label: "Revenue",
      dashed: false,
    },
    {
      key: "rentGross",
      color: "hsl(0 72% 51%)",
      label: "Rent (gross)",
      dashed: false,
    },
    {
      key: "variableCosts",
      color: "hsl(25 85% 55%)",
      label: "Variable costs",
      dashed: true,
    },
    {
      key: "fixedOpex",
      color: "hsl(45 80% 50%)",
      label: "Fixed opex",
      dashed: true,
    },
    {
      key: "fitoutAmortisation",
      color: "hsl(280 60% 60%)",
      label: "Fitout amort.",
      dashed: true,
    },
    {
      key: "reddamEbitda",
      color: "hsl(142 76% 50%)",
      label: "EBITDA",
      dashed: false,
    },
  ] as const;

  series.forEach((s) => {
    const points = years
      .map((y, i) => {
        const x = xScale(i);
        const yv = yScale(y[s.key as keyof typeof y] as number);
        return `${x.toFixed(1)},${yv.toFixed(1)}`;
      })
      .join(" ");

    const poly = document.createElementNS(SVG_NS, "polyline");
    poly.setAttribute("points", points);
    poly.setAttribute("fill", "none");
    poly.setAttribute("stroke", s.color);
    poly.setAttribute("stroke-width", s.dashed ? "1.8" : "2.5");
    poly.setAttribute("stroke-linecap", "round");
    poly.setAttribute("stroke-linejoin", "round");
    if (s.dashed) {
      poly.setAttribute("stroke-dasharray", "4 3");
    }
    svg.appendChild(poly);
  });

  // X-axis labels
  years.forEach((y, i) => {
    const x = xScale(i);
    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", String(x));
    label.setAttribute("y", String(height - 12));
    label.setAttribute("text-anchor", "middle");
    label.setAttribute("font-size", "10");
    label.setAttribute("fill", "#94a3b8");
    label.textContent = `Y${y.year}`;
    svg.appendChild(label);
  });

  // Legend
  const legendY = 14;
  const legendSpacing = 82;
  series.forEach((s, i) => {
    const lx = pad.l + i * legendSpacing;
    const dot = document.createElementNS(SVG_NS, "rect");
    dot.setAttribute("x", String(lx));
    dot.setAttribute("y", String(legendY - 6));
    dot.setAttribute("width", "10");
    dot.setAttribute("height", s.dashed ? "2" : "3");
    dot.setAttribute("fill", s.color);
    dot.setAttribute("rx", "1");
    svg.appendChild(dot);

    const lbl = document.createElementNS(SVG_NS, "text");
    lbl.setAttribute("x", String(lx + 14));
    lbl.setAttribute("y", String(legendY + 3));
    lbl.setAttribute("font-size", "10");
    lbl.setAttribute("fill", "#94a3b8");
    lbl.textContent = s.label;
    svg.appendChild(lbl);
  });

  return svg;
}

/**
 * Generate a diagonal watermark PNG data URL.
 * Draws rotated text on a canvas sized for A4 at 2× DPI.
 */
export function generateWatermarkDataUrl(
  text = "CONFIDENTIAL",
  color = "rgba(180, 188, 203, 0.14)",
  fontSize = 72,
): string | null {
  const canvas = document.createElement("canvas");
  const width = 1190; // ~A4 width at 2× 72 DPI
  const height = 1684; // ~A4 height at 2× 72 DPI
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    // A stretched placeholder pixel can tint every content page. In headless
    // environments, omit the decorative watermark instead.
    canvas.width = 0;
    canvas.height = 0;
    return null;
  }

  ctx.save();
  ctx.translate(width / 2, height / 2);
  ctx.rotate(-Math.PI / 4);
  ctx.font = `600 ${fontSize}px Roboto, Arial, sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Tile the text across the rotated canvas
  const stepX = fontSize * 6;
  const stepY = fontSize * 4;
  const cols = 5;
  const rows = 7;
  for (let r = -rows; r <= rows; r++) {
    for (let c = -cols; c <= cols; c++) {
      ctx.fillText(text, c * stepX, r * stepY);
    }
  }
  ctx.restore();

  const dataUrl = canvas.toDataURL("image/png");
  canvas.width = 0;
  canvas.height = 0;
  return dataUrl;
}

export interface WaterfallItem {
  label: string;
  value: number;
}

export function renderWaterfallSvg(
  items: WaterfallItem[],
  total: number,
  proceeds?: number | null,
  width = 900,
  height = 320,
  valueFormat: (v: number) => string = formatTickShort,
): SVGSVGElement {
  const pad = { l: 72, r: 20, t: 28, b: 92 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  svg.setAttribute("xmlns", SVG_NS);
  svg.setAttribute("font-family", "Roboto, Arial, sans-serif");
  svg.style.fontFamily = "Roboto, Arial, sans-serif";

  const totalSum = items.reduce((a, b) => a + b.value, 0);
  const totalVal = total ?? totalSum;
  const yMax = Math.max(totalVal, proceeds ?? 0) * 1.15;
  const yMin =
    proceeds !== null && proceeds !== undefined
      ? Math.min(0, proceeds - totalVal) * 1.1
      : 0;
  const ticks = niceTicks(yMin, yMax, 5);
  const yLo = ticks[0] ?? yMin;
  const yHi = ticks.at(-1) ?? yMax;
  const yScale = (y: number) =>
    pad.t + innerH - ((y - yLo) / Math.max(1e-9, yHi - yLo)) * innerH;

  const colCount =
    items.length + (proceeds !== null && proceeds !== undefined ? 2 : 1);
  const barW = innerW / colCount - 10;
  const accents = ["#a37a2c", "#5b6779", "#7e6a3f", "#7c8898"];

  // Grid lines
  ticks.forEach((tk) => {
    const line = document.createElementNS(SVG_NS, "line");
    line.setAttribute("x1", String(pad.l));
    line.setAttribute("x2", String(width - pad.r));
    line.setAttribute("y1", String(yScale(tk)));
    line.setAttribute("y2", String(yScale(tk)));
    line.setAttribute("stroke", "hsl(220 15% 25% / 0.2)");
    line.setAttribute("stroke-dasharray", "2 3");
    svg.appendChild(line);

    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", String(pad.l - 8));
    label.setAttribute("y", String(yScale(tk) + 4));
    label.setAttribute("text-anchor", "end");
    label.setAttribute("font-size", "11");
    label.setAttribute("fill", "#475569");
    label.textContent = valueFormat(tk);
    svg.appendChild(label);
  });

  // Zero line
  const zeroY = yScale(0);
  const zeroLine = document.createElementNS(SVG_NS, "line");
  zeroLine.setAttribute("x1", String(pad.l));
  zeroLine.setAttribute("x2", String(width - pad.r));
  zeroLine.setAttribute("y1", String(zeroY));
  zeroLine.setAttribute("y2", String(zeroY));
  zeroLine.setAttribute("stroke", "hsl(220 10% 55% / 0.5)");
  svg.appendChild(zeroLine);

  // Cost item bars (incremental stack)
  let running = 0;
  items.forEach((it, i) => {
    const prev = running;
    running += it.value;
    const x = pad.l + (innerW / colCount) * i + 5;
    const top = yScale(running);
    const h = Math.max(0, yScale(prev) - top);

    const g = document.createElementNS(SVG_NS, "g");

    const rect = document.createElementNS(SVG_NS, "rect");
    rect.setAttribute("x", String(x));
    rect.setAttribute("y", String(yScale(prev) - h));
    rect.setAttribute("width", String(barW));
    rect.setAttribute("height", String(h));
    rect.setAttribute("fill", accents[i % accents.length]);
    rect.setAttribute("rx", "3");
    g.appendChild(rect);

    // Label
    const lbl = document.createElementNS(SVG_NS, "text");
    lbl.setAttribute("x", String(x + barW / 2));
    lbl.setAttribute("y", String(height - 66));
    lbl.setAttribute("text-anchor", "end");
    lbl.setAttribute("font-size", "12");
    lbl.setAttribute("fill", "#334155");
    lbl.setAttribute("font-weight", "500");
    lbl.setAttribute("transform", `rotate(-38 ${x + barW / 2} ${height - 66})`);
    lbl.textContent = it.label;
    g.appendChild(lbl);

    // Value label
    const val = document.createElementNS(SVG_NS, "text");
    val.setAttribute("x", String(x + barW / 2));
    val.setAttribute("y", String(yScale(prev) - h - 6));
    val.setAttribute("text-anchor", "middle");
    val.setAttribute("font-size", "12");
    val.setAttribute("fill", "#334155");
    val.textContent = valueFormat(it.value);
    g.appendChild(val);

    // Connector line
    if (i < items.length - 1) {
      const conn = document.createElementNS(SVG_NS, "line");
      conn.setAttribute("x1", String(x + barW));
      conn.setAttribute("y1", String(yScale(running)));
      conn.setAttribute("x2", String(x + barW + 10));
      conn.setAttribute("y2", String(yScale(running)));
      conn.setAttribute("stroke", "hsl(220 15% 35%)");
      conn.setAttribute("stroke-dasharray", "2 3");
      g.appendChild(conn);
    }

    svg.appendChild(g);
  });

  // Total bar
  const totalIdx = items.length;
  const totalX = pad.l + (innerW / colCount) * totalIdx + 5;
  const totalH = Math.max(0, yScale(0) - yScale(totalVal));
  const totalG = document.createElementNS(SVG_NS, "g");

  const totalRect = document.createElementNS(SVG_NS, "rect");
  totalRect.setAttribute("x", String(totalX));
  totalRect.setAttribute("y", String(yScale(0) - totalH));
  totalRect.setAttribute("width", String(barW));
  totalRect.setAttribute("height", String(totalH));
  totalRect.setAttribute("fill", "#edf1f5");
  totalRect.setAttribute("stroke", "#18324d");
  totalRect.setAttribute("stroke-width", "1");
  totalRect.setAttribute("rx", "3");
  totalG.appendChild(totalRect);

  const totalLbl = document.createElementNS(SVG_NS, "text");
  totalLbl.setAttribute("x", String(totalX + barW / 2));
  totalLbl.setAttribute("y", String(height - 12));
  totalLbl.setAttribute("text-anchor", "middle");
  totalLbl.setAttribute("font-size", "10");
  totalLbl.setAttribute("fill", "#18324d");
  totalLbl.setAttribute("font-weight", "600");
  totalLbl.textContent = "Total";
  totalG.appendChild(totalLbl);

  const totalValLbl = document.createElementNS(SVG_NS, "text");
  totalValLbl.setAttribute("x", String(totalX + barW / 2));
  totalValLbl.setAttribute("y", String(yScale(totalVal) - 6));
  totalValLbl.setAttribute("text-anchor", "middle");
  totalValLbl.setAttribute("font-size", "12");
  totalValLbl.setAttribute("fill", "#18324d");
  totalValLbl.setAttribute("font-weight", "600");
  totalValLbl.textContent = valueFormat(totalVal);
  totalG.appendChild(totalValLbl);

  svg.appendChild(totalG);

  // Proceeds bar + profit bridge
  if (proceeds !== null && proceeds !== undefined) {
    const proceedsIdx = items.length + 1;
    const proceedsX = pad.l + (innerW / colCount) * proceedsIdx + 5;
    const proceedsH = Math.max(0, yScale(0) - yScale(proceeds));
    const proceedsG = document.createElementNS(SVG_NS, "g");

    const proceedsRect = document.createElementNS(SVG_NS, "rect");
    proceedsRect.setAttribute("x", String(proceedsX));
    proceedsRect.setAttribute("y", String(yScale(0) - proceedsH));
    proceedsRect.setAttribute("width", String(barW));
    proceedsRect.setAttribute("height", String(proceedsH));
    proceedsRect.setAttribute("fill", "#eaf4ee");
    proceedsRect.setAttribute("stroke", "#2f6b4f");
    proceedsRect.setAttribute("stroke-width", "1");
    proceedsRect.setAttribute("rx", "3");
    proceedsG.appendChild(proceedsRect);

    const proceedsLbl = document.createElementNS(SVG_NS, "text");
    proceedsLbl.setAttribute("x", String(proceedsX + barW / 2));
    proceedsLbl.setAttribute("y", String(height - 12));
    proceedsLbl.setAttribute("text-anchor", "middle");
    proceedsLbl.setAttribute("font-size", "10");
    proceedsLbl.setAttribute("fill", "#2f6b4f");
    proceedsLbl.setAttribute("font-weight", "600");
    proceedsLbl.textContent = "Proceeds";
    proceedsG.appendChild(proceedsLbl);

    const proceedsValLbl = document.createElementNS(SVG_NS, "text");
    proceedsValLbl.setAttribute("x", String(proceedsX + barW / 2));
    proceedsValLbl.setAttribute("y", String(yScale(proceeds) - 6));
    proceedsValLbl.setAttribute("text-anchor", "middle");
    proceedsValLbl.setAttribute("font-size", "12");
    proceedsValLbl.setAttribute("fill", "#2f6b4f");
    proceedsValLbl.setAttribute("font-weight", "600");
    proceedsValLbl.textContent = valueFormat(proceeds);
    proceedsG.appendChild(proceedsValLbl);

    // Profit bridge
    const profit = proceeds - totalVal;
    if (Math.abs(profit) > 0) {
      const bridgeY =
        yScale(totalVal) + (yScale(proceeds) - yScale(totalVal)) / 2;
      const bridge = document.createElementNS(SVG_NS, "line");
      bridge.setAttribute("x1", String(totalX + barW));
      bridge.setAttribute("y1", String(bridgeY));
      bridge.setAttribute("x2", String(proceedsX));
      bridge.setAttribute("y2", String(bridgeY));
      bridge.setAttribute(
        "stroke",
        profit >= 0 ? "hsl(142 76% 50%)" : "hsl(0 72% 51%)",
      );
      bridge.setAttribute("stroke-width", "1.5");
      bridge.setAttribute("stroke-dasharray", "4 3");
      proceedsG.appendChild(bridge);

      const bridgeLabel = document.createElementNS(SVG_NS, "text");
      bridgeLabel.setAttribute("x", String((totalX + barW + proceedsX) / 2));
      bridgeLabel.setAttribute("y", String(bridgeY - 6));
      bridgeLabel.setAttribute("text-anchor", "middle");
      bridgeLabel.setAttribute("font-size", "10");
      bridgeLabel.setAttribute(
        "fill",
        profit >= 0 ? "hsl(142 76% 50%)" : "hsl(0 72% 51%)",
      );
      bridgeLabel.textContent = (profit >= 0 ? "+" : "") + valueFormat(profit);
      proceedsG.appendChild(bridgeLabel);
    }

    svg.appendChild(proceedsG);
  }

  return svg;
}

/** Capture the waterfall chart SVG by rendering programmatically. */
export async function captureWaterfallChart(
  items: WaterfallItem[],
  total: number,
  proceeds?: number | null,
  scale = 2,
  valueFormat?: (v: number) => string,
): Promise<string | null> {
  if (!items.length) return null;
  const svg = renderWaterfallSvg(
    items,
    total,
    proceeds,
    1000,
    442,
    valueFormat,
  );
  return svgElementToPngDataUrl(svg, scale);
}

/** Capture the tornado chart SVG specifically (by data-chart attribute).
 *  If `rows` and `base` are provided, renders a temporary SVG instead of querying the DOM.
 */
export function reportTornadoRowHeight(rowCount: number): number {
  if (rowCount <= 0) return 36;
  return Math.min(52, Math.max(36, 416 / rowCount));
}

export async function captureTornadoChart(
  scale = 2,
  rows?: Array<{ label: string; low: number; high: number }>,
  base?: number,
  valueFormat?: (v: number) => string,
  presentationBound?: number,
  deltaFormat?: (v: number) => string,
): Promise<string | null> {
  if (rows && rows.length && base !== undefined) {
    const svg = renderTornadoSvg(
      rows,
      base,
      760,
      reportTornadoRowHeight(rows.length),
      valueFormat,
      presentationBound,
      deltaFormat,
    );
    return svgElementToPngDataUrl(svg, scale);
  }
  const el = document.querySelector(
    'svg[data-chart="tornado"]',
  ) as SVGSVGElement | null;
  if (!el) return null;
  return svgElementToPngDataUrl(el, scale);
}
